import React from "react"
import { motion } from "framer-motion"
import { useCarousel } from "../../../../hooks/useEmblaCarousel"

const Categories: React.FC = () => {
  const { emblaRef, selectedIndex, onDotButtonClick } = useCarousel({
    options: {
      loop: false,
      align: "center",
      skipSnaps: false,
    },
  })

  const capitalizeFirstLetter = (text: string) => {
    return text
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  }

  const categories = [
    {
      id: 1,
      title: "Nguyên liệu giữ trọn hơi thở đất trời",
      subtitle:
        "Mỗi loại rau, mỗi nhánh nấm đều được lựa chọn khi đạt độ tươi tốt nhất, như đang còn thở cùng đất. Chúng tôi tin rằng món ăn bắt đầu từ sự trân quý từng hạt giống, từng mùa vụ. Từ đồng ruộng đến bàn ăn, hành trình ấy là một vòng tuần hoàn nuôi dưỡng, để mỗi thực khách không chỉ ăn mà còn cảm nhận sự sống đang tiếp nối trong mình.",
      image: "/web-ttmi/homepage/categories/nguyen-lieu.jpg",
    },
    {
      id: 2,
      title: "Hương chay nâng niu vị giác",
      subtitle:
        "Ẩm thực ở Thiền Trúc không chỉ là vị ngon nơi đầu lưỡi. Đó là hương của nắng, mùi của đất ẩm, vị ngọt trong trẻo từ rau củ và sự an yên khi thưởng thức. Mỗi món ăn được chế biến như một nghi thức, để người dùng chậm rãi cảm nhận, để vị giác và tâm trí cùng lúc mở ra, trở về với sự tỉnh thức ban sơ.",
      image: "/web-ttmi/homepage/categories/huong-vi.jpg",
    },
    {
      id: 3,
      title: "Trải nghiệm như một buổi Thiền",
      subtitle:
        "Một bữa ăn tại Thiền Trúc là một hành trình trọn vẹn: từ khi bước vào không gian tĩnh, ngồi xuống, hít thở sâu, đến khi nếm từng món. Mỗi buổi tiệc, mỗi thực đơn, mỗi góc không gian đều được sắp đặt để khơi gợi sự tĩnh tại, như một bài thiền giữa đời sống hối hả. Ở đây, chúng tôi không chỉ dọn món, chúng tôi dọn một không gian để tâm an trú.",
      image: "/web-ttmi/homepage/categories/trai-nghiem.jpg",
    },
  ]

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="px-6 sm:px-8 lg:px-[30px]">
        <div className="max-w-[1700px] mx-auto">
          {/* Desktop Product Cards */}
          <div className="hidden lg:grid grid-cols-3 gap-3">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group cursor-default"
              >
                <div className="relative w-full aspect-[4/5] max-w-[400px] overflow-hidden mx-auto">
                  {/* Background Image */}
                  <img
                    src={category.image}
                    alt={category.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="lg:hidden">
            <div className="embla overflow-hidden" ref={emblaRef}>
              <div className="embla__container flex">
                {categories.map((category, index) => (
                  <div
                    key={category.id}
                    className="embla__slide flex-[0_0_100%] min-w-0 px-6 sm:px-8"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.2 }}
                      viewport={{ once: true }}
                      className="group cursor-default"
                    >
                      {/* Mobile Category Header */}
                      <div className="text-center mb-6 sm:mb-8">
                        <h3 className="text-3xl sm:text-4xl font-bold">
                          {capitalizeFirstLetter(category.title)}
                        </h3>
                      </div>

                      {/* Mobile Product Card */}
                      <div className="relative w-full max-w-[350px] sm:max-w-[400px] h-[450px] sm:h-[500px] overflow-hidden mx-auto">
                        <img
                          src={category.image}
                          alt={category.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Navigation Dots */}
            <div className="flex justify-center mt-8 sm:mt-10 space-x-2">
              {categories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all duration-300 ease-in-out transform hover:scale-110 ${
                    selectedIndex === index
                      ? "opacity-100 shadow-md"
                      : "opacity-40 hover:opacity-60"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Category Headers - Desktop Grid */}
          <div className="hidden lg:grid grid-cols-3 gap-4 xl:gap-4 mt-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className=" max-w-[400px] mx-auto "
              >
                <h3 className="text-2xl font-bold text-primary min-h-[60px]">
                  {capitalizeFirstLetter(category.title)}
                </h3>
                <p className="text-base text-primary mt-2">
                  {category.subtitle}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Categories
